// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesigner.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import 'share/utils/Izenda.StringUtilities';
import { observable, toJS, action, computed } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';

//child model
import ReportDesignerDataSource from './Izenda.ReportDesignerDataSource';
import ReportDesignerFields from './Izenda.ReportDesignerFields';
import ReportDesignerFormats from './Izenda.ReportDesignerFormats';
import ReportDesignerAccess from 'logic/models/Izenda.Access';
import ReportDesignerExport from 'logic/models/Izenda.ExportConfigModel';

// Constant
import {
  ADVANCED,
  SIMPLE,
  getReportSection,
  EMPTY_GUID,
  DEFAULT_PREVIEW_RECORD,
  DEFAULT_REPORT_NAME,
  REPORT_TYPE_ENUM,
  TYPES
} from '../data/Izenda.ReportDesignerConstants';
import CategoryMetadata from 'logic/models/Izenda.CategoryMetadata';
import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import uiState from 'logic/Izenda.UiState.js';
import ReportViewerMode from 'logic/models/Izenda.ReportViewerMode';
import ReportDesignerRelationshipDetail from './Izenda.ReportDesignerRelationshipDetail';
import {
  haveSelectedAtLeastOneQuerySource,
  getSelectedQuerySources
} from 'modules/ReportDesigner/DataSource/Izenda.DataTreeUtils';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';

import { isNil, get, getOr, isEmpty, flow, filter, forEach, find, findIndex, last, map, flatten } from 'lodash/fp';

@tracker
class ReportDesigner {
  @observable saveAs = false;
  @observable previewRecord = DEFAULT_PREVIEW_RECORD;
  /*common data for report data*/
  @observable reportName = '';
  @observable reportType = REPORT_TYPE_ENUM.TEMPLATE;
  @observable categoryId = null;
  @observable categoryName = '';
  @observable subCategoryId = null;
  @observable subCategoryName = '';
  @observable ignoreCheckChange = true;
  @observable isSavingReport = true;
  @observable types = TYPES;
  @observable categories = [];
  @observable subCategories = [];
  @observable errorMessages = [];
  @observable expandedLevel = 0; // First level
  @observable version = 0;
  @observable schedules = [];
  @observable owner = '';
  @observable ownerId = '';
  @observable tenantId = null;
  @observable exportFormatSetting = null;
  @observable excludedRelationships = null;
  @observable createdBy = '';
  @observable reportDataSource = [];
  @observable reportId = null;
  @observable dynamicQuerySourceFields = [];
  @observable createdById = null;
  @observable accessPriority = null;
  @observable active = true;
  @observable isGlobal = false;
  @observable allowToSave = null;
  @observable showAdvancedOption = false;
  isMaintainState = false;
  @observable reportMode = 0;
  @observable id = "";
  constructor(viewMode: boolean, reportId: string, isReload: boolean, editMode: boolean) {
    this.dataSource = new ReportDesignerDataSource();
    this.reportFields = new ReportDesignerFields(viewMode, isReload, editMode);
    this.reportFormats = new ReportDesignerFormats();
    this.reportAccess = new ReportDesignerAccess();
    this.displayMode = new ReportViewerMode();
    this.subReportFilterFromQuery = {};
    this.subReportFilter = {};
    this.subReportMappingFields = [];
    this.export = new ReportDesignerExport();
    // draft data id, use to get draft report definition
    this.draftId = null;
    // printingMode, use for printing
    this.printingMode = false;
    // default image logo for report
    this.defaultImageUrl = {};

    this.reportName = isNil(reportId) ? DEFAULT_REPORT_NAME : '';
    this.reportId = reportId || null;

    this.cleanDirty();
  }

  @computed
  get accesses() {
    return this.reportAccess.accessList;
  }

  @computed
  get updateAccesses() {
    return this.reportAccess.draftAccesses;
  }

  @computed
  get supportReportParts() {
    return get('reportBody.supportReportParts', this.reportFields);
  }

  @computed
  get configReportPart() {
    return getOr(null, 'reportBody.configReportPart', this.reportFields);
  }

  @computed
  get hasSelectedDataSource() {
    return haveSelectedAtLeastOneQuerySource(this.dataSource.dataTree.selectedTree);
  }

  /**
   * Check if report is ready for call getReportPartData
   * Avoid case navigate from master report to sub report in intergration mode
   */
  @computed
  get isReadyForSubReport() {
    return isEmpty(this.subReportFilterFromQuery) || !isEmpty(this.subReportFilter);
  }

  /**
   * Update report info when load for edit
   * @param {object} report data
   */
  @action('read')
  read(report: any) {

    const category = report.category || {};
    const subCategory = report.subCategory || {};
    this.id = report.id;
    this.reportType = report.type;
    this.categoryId = category.id;
    this.categoryName = category.name;
    this.subCategoryId = subCategory.id;
    this.subCategoryName = subCategory.name;
    this.reportName = report.name;
    this.reportMode = report.reportMode;
    this.previewRecord = report.previewRecord;
    this.reportDataSource = report.reportDataSource;
    this.reportRelationship = report.reportRelationship;
    this.owner = report.owner;
    this.ownerId = report.ownerId;
    this.version = report.version;
    this.tenantId = report.tenantId;
    this.createdById = report.createdById;
    this.accessPriority = report.accessPriority;
    this.active = report.active;
    this.dynamicQuerySourceFields = report.dynamicQuerySourceFields;
    this.excludedRelationships = report.excludedRelationships;
    this.isGlobal = !isNil(report.isGlobal) ? report.isGlobal : false;
    this.allowToSave = report.allowToSave;

    this.reportFields.readFromReport(report);
    this.reportFormats.readFromReport(report);
    this.export.readFromReport(report);

    this.updateAdvanceDataSource(report);
    this.dataSource.readReportMode(report.advancedMode ? ADVANCED : SIMPLE);
    this.dataSource.readDistinct(report.isDistinct);

    this.cleanDirty();
  }

  /**
   * Set report part title
   * @return {any} report 
   */
  setReportPartTitle(value: string): any {
    let reportFieldsData = this.reportFields.buildDataForSaving(this.reportId, this.isMaintainState);
    reportFieldsData.reportPart[0].reportPartContent.title.text = value;
  }

  /**
   * Build save data object
   * @param {any} section
   * @param {boolean} removeDynamicThreshold
   * @returns {object} save object
   */
  getReportDefinition(section: any): any {
    const generalData = this.getGeneralData();
    const reportFieldsData = this.reportFields.buildDataForSaving(this.reportId, this.isMaintainState);
    const reportFormats = this.buildReportFormatsForSaving();
    const schedules = this.buildScheduleDataForSaving();
    const exportFormatSetting = this.export.getExportConfig();
    this.reportAccess.mergeWithReportDraftAccess();
    const advancedMode = this.dataSource.reportMode === ADVANCED;
    let reportDataSource;
    let reportRelationship;

    if (this.draftId) {
      reportDataSource = this.reportDataSource;
      reportRelationship = this.reportRelationship;
    } else {
      const selectedQuerySources = getSelectedQuerySources(this.dataSource.dataTree.selectedTree);
      reportDataSource = !isEmpty(selectedQuerySources) ? selectedQuerySources : this.reportDataSource;
      reportRelationship = advancedMode ? this.dataSource.dataSourceDetail.dataSourceGrid : [];
    }

    return {
      reportKey: {
        key: this.reportId,
        modified: null,
        tenantId: uiState.getTenantId()
      },
      section: getReportSection(section),
      saveAs: this.saveAs,
      ignoreCheckChange: this.ignoreCheckChange,
      isSavingReport: this.isSavingReport,
      report: {
        name: generalData.name,
        type: generalData.type,
        reportMode: this.reportMode,
        previewRecord: this.previewRecord,
        advancedMode: advancedMode,
        allowNulls: this.dataSource.allowNull,
        isDistinct: this.dataSource.distinct,
        categoryId: generalData.category.id,
        categoryName: generalData.category.name,
        category: generalData.category,
        subCategory: generalData.subCategory,
        subCategoryName: generalData.subCategory.name,
        subCategoryId: generalData.subCategory.id,
        reportDataSource: reportDataSource,
        reportRelationship: reportRelationship,
        reportFilter: reportFieldsData.reportFilter,
        reportPart: reportFieldsData.reportPart,
        header: reportFormats.header,
        footer: reportFormats.footer,
        titleDescription: reportFormats.titleDescription,
        version: this.version,
        schedules: schedules,
        owner: this.owner,
        ownerId: this.ownerId,
        accesses: this.updateAccesses,
        exportFormatSetting: exportFormatSetting,
        createdById: this.createdById,
        dynamicQuerySourceFields: this.dynamicQuerySourceFields,
        snapToGrid: reportFieldsData.snapToGrid,
        excludedRelationships: this.excludedRelationships,
        isGlobal: this.isGlobal,
        allowToSave: this.allowToSave,
        calculatedFields: this.reportFields.dataTree.calculatedFields
      },
      expandedLevel: this.expandedLevel
    };
  }

  removeUnuseDynamicThresholds() {
    this.reportFields.reportBody.removeUnuseDynamicThresholds();
  }

  /**
   * Get report relationships
   * @return {any} report relationships
   */
  getReportRelationship(): any {
    if (this.draftId) {
      return this.reportRelationship;
    }
    return this.dataSource.reportMode === ADVANCED ? this.dataSource.dataSourceDetail.dataSourceGrid : [];
  }

  /**
   * Update alias for all key join filter relationships
   * @param oldAlias {string} old value
   * @param newAlias {string} new value
   */
  @action('updateRelationKeyJoinFilterAlias')
  updateRelationKeyJoinFilterAlias(oldAlias: string, newAlias: string) {
    const relationships = this.getReportRelationship();
    const allFilterKeyJoins = flow(
      map(x => x.relationshipKeyJoins.toJS()),
      flatten,
      filter({
        comparisonOperator: '= (Filter)',
        foreignFilterAlias: oldAlias
      }),
    )(relationships || []);
    forEach(x => x.foreignFilterAlias = newAlias, allFilterKeyJoins);
  }


  /**
   * Build Report Format data when saving
   * @return {any} data format
   */
  buildReportFormatsForSaving(): any {
    return {
      header: {
        visible: this.reportFormats.getHeaderFooterVisible(),
        items: this.reportFields.reportHeader.getItems()
      },
      footer: {
        visible: this.reportFormats.getHeaderFooterVisible(),
        items: this.reportFields.reportFooter.getItems()
      },
      titleDescription: {
        visible: this.reportFormats.getTitleDescriptionVisible(),
        items: this.reportFields.reportTitleDescription.getItems()
      }
    };
  }

  /**
   * add filters for sub report
   * @param  {any} filters
   * @param  {boolean} fromQuery
   */
  setSubReportFilters(filters: any, fromQuery: boolean): void {
    fromQuery ? (this.subReportFilterFromQuery = filters) : (this.subReportFilter = filters);
  }

  /**
   * Parse url.
   * @param {any} query
   */
  readURLOptions(query: any): void {
    this.printingMode = !!getOr(false, 'print', query);
    this.printingMode && (this.draftId = getOr(null, 'draftId', query));
  }

  /**
   * Build Schedule data when saving
   * @return {any} schedule data
   */
  buildScheduleDataForSaving(): any {
    if (this.isGlobal) {
      return [];
    }
    const schedules = flow(
      filter(item => item.state !== ObjectStateConstants.NONE),
      forEach(i => {
        if (i.state === ObjectStateConstants.INSERT && !String.isGuidFormat(i.id)) {
          i.id = null;
        }
      })
    )(this.schedules);

    return schedules;
  }

  /**
   * Update preview record
   * @param  {any} value
   */
  @action('updatePreviewRecord')
  updatePreviewRecord(value: any) {
    this.previewRecord = parseInt(value);
    this.markDirty();
  }


  /**
   * Update report save mode 0 for reportDesigner 1 for explorer
   * @param  {any} value
   */
  @action('updateReportMode')
  updateReportMode(value: any) {
    this.reportMode = parseInt(value);
    this.markDirty();
  }

  /**
   * update report name in save popup
   * @param  {string} value
   */
  @action('changeName')
  changeName(value: string) {
    this.reportName = value;
    this.markDirty();
  }

  /**
   * reset report id in save popup for NLQ
   * @param  {string} value
   */
  @action('resetReportKey')
  resetReportKey(value: string) {
    this.reportId = value;
    this.markDirty();
  }

  /**
   * Update data source grid advance
   * @param  {any} reportInfo
   */
  @action('updateAdvanceDataSource')
  updateAdvanceDataSource(reportInfo: any) {
    const reportRelationship = get('reportRelationship', reportInfo);

    if (!isEmpty(reportRelationship)) {
      let relationships = [];
      for (let index = 0; index < reportRelationship.length; index++) {
        relationships.push(new ReportDesignerRelationshipDetail(reportRelationship[index]));
      }
      this.dataSource.dataSourceDetail.dataSourceGrid = relationships;
      this.dataSource.dataSourceDetail.dataSourceGrid.forEach(dataSourceGridItem => {
        dataSourceGridItem.level = dataSourceGridItem.relationshipPosition + 1;
        dataSourceGridItem.reindexKeyJoinsPosition();
      });
    }
  }

  /**
   * Update report detail.
   * @param {string} reportKey
   * @param {any} reportInfo
   * @param {boolean} isAdvancedMode
   * @param {boolean} keepReportBodyStates
   */
  @action('updateReportInfo')
  updateReportInfo(reportKey: string, reportInfo: any, isAdvancedMode: boolean, keepReportBodyStates: boolean, targetReportPart: any = null) {
    this.reportId = reportKey;
    this.version = reportInfo.version;

    this.reportFields.readFromReport(reportInfo, keepReportBodyStates, targetReportPart);

    //update report info
    this.isGlobal = !isNil(reportInfo.isGlobal) ? reportInfo.isGlobal : false;
    this.categoryId = reportInfo.categoryId;
    this.categoryName = reportInfo.categoryName;
    this.subCategoryId = reportInfo.subCategoryId;
    this.subCategoryName = reportInfo.subCategoryName;
    //Update schedules
    this.schedules = reportInfo.schedules;
    this.owner = reportInfo.owner;
    this.ownerId = reportInfo.ownerId;

    this.createdBy = reportInfo.createdBy;
    this.reportAccess.populateData({
      accessList: reportInfo.accesses
    });

    isAdvancedMode && this.updateAdvanceDataSource(reportInfo);
  }

  /**
   * Update report type in save popup
   * @param  {number} value
   */
  @action('updateType')
  updateType(value: number) {
    this.reportType = value;
    this.updateCategory();
    this.updateSubCategory();
    this.markDirty();
  }

  /**
   * Update report's category in save popup
   * @param  {string} id
   * @param  {string} name
   */
  @action('updateCategory')
  updateCategory(id: string, name: string) {
    this.categoryId = id || null;
    this.categoryName = name || '';
    this.markDirty();
  }

  /**
   * Get tenant id of category.
   * @param  {string} id
   * @param  {boolean} isCategory
   * @return {any} tenantId
   */
  getTenantIdOfCategory(id: string, isCategory: boolean): any {
    if (!id) {
      return uiState.getTenantId();
    }

    const dataSource = isCategory ? this.categories : this.subCategories;
    const matchingItem = find(['id', id], dataSource);

    if (matchingItem) {
      return matchingItem.tenantId;
    }

    return null;
  }

  /**
   * Update errorMessages
   * @param  {any} errorMessages
   */
  updateErrorMessages(errorMessages: any): void {
    this.errorMessages = errorMessages;
  }

  /**
   * Update report's sub-category in save popup
   * @param  {string} id
   * @param  {string} name
   */
  @action('updateSubCategory')
  updateSubCategory(id: string, name: string) {
    this.subCategoryId = id || null;
    this.subCategoryName = name || '';
    this.markDirty();
  }

  /**
   *  Get data for validate report name
   * @return {any}
   */
  getGeneralData(): any {
    let result = {
      id: this.reportId,
      name: this.reportName,
      type: this.reportType,
      category: {},
      subCategory: {}
    };

    if (!isNil(this.categoryName)) {
      result.category = {
        id: String.isGuidFormat(this.categoryId) && this.categoryId !== EMPTY_GUID ? this.categoryId : null,
        name: this.categoryName,
        type: this.reportType
      };

      result.category.tenantId = this.getTenantIdOfCategory(result.category.id, true);
    }

    if (!isNil(this.subCategoryName)) {
      result.subCategory = {
        id: String.isGuidFormat(this.subCategoryId) && this.categoryId !== EMPTY_GUID ? this.subCategoryId : null,
        name: this.subCategoryName,
        type: this.reportType
      };

      result.subCategory.tenantId = this.getTenantIdOfCategory(result.subCategory.id, false);
    }

    result.category.isGlobal = this.isGlobal;
    result.subCategory.isGlobal = this.isGlobal;
    result.tenantId = this.tenantId;
    return result;
  }

  /**
   * Add new sub-category in save popup
   * @param  {any} subCategory
   */
  @action('addSubCategory')
  addSubCategory(subCategory: any) {
    if (findIndex(subCategory, this.subCategories) < 0) {
      this.subCategories.push(subCategory);
    }
    this.markDirty();
  }

  /**
   * Add new category in save popup
   * @param  {any} category
   */
  @action('addCategory')
  addCategory(category: any) {
    if (findIndex(category, this.categories) < 0) {
      this.categories.push(category);
    }
    this.updateSubCategory();
    this.subCategories = [];
    this.markDirty();
  }

  /**
   * Map data from data return from api to model sub-category
   * @param  {array} result
   * @return {Promise}
   */
  readSubCategoryData(result: Array): any {
    result = result || [];
    this.subCategories = result.map(item => new CategoryMetadata(item));

    return Promise.resolve('');
  }

  /**
   * Map data from data return from api to model category
   * @param  {array} result
   * @return {any}
   */
  readCategoryData(result: Array): any {
    result = result || [];
    this.categories = result.map(item => new CategoryMetadata(item));
    return null;
  }

  /**
   * Mark save as for saving report
   */
  markSaveAs(): void {
    this.saveAs = true;
  }

  /**
   * Reset save as
   */
  resetSaveAs(): void {
    this.saveAs = false;
  }

  /**
   * Get save permission.
   * @returns {boolean} permission
   */
  checkSavePermission(): boolean {
    return (
      (this.accessPriority === ACCESS_RIGHT.FullAccess && !this.isGlobal) ||
      (this.isGlobal && uiState.currentUser.systemAdmin && !uiState.tenantId)
    );
  }

  /**
   * Get dirty status.
   * @return {boolean}
   */
  checkDirty(): boolean {
    return (
      this.isDirty ||
      (this.dataSource.checkDirty && this.dataSource.checkDirty()) ||
      (this.reportFields.checkDirty && this.reportFields.checkDirty()) ||
      (this.reportFormats.checkDirty && this.reportFormats.checkDirty()) ||
      this.reportAccess.checkDirty() ||
      this.export.checkDirty() ||
      this.checkScheduleDirty()
    );
  }

  /**
   * Clean dirty status.
   */
  @action('cleanDirty')
  cleanDirty() {
    this.dataSource.cleanDirty && this.dataSource.cleanDirty();
    this.reportFields.cleanDirty && this.reportFields.cleanDirty();
    this.reportFormats.cleanDirty && this.reportFormats.cleanDirty();
    this.reportAccess.cleanDirty();
    this.export.cleanDirty();
    this.resetDirty();
  }

  /**
   * Check schedule dirty
   * @return {bolean}
   */
  checkScheduleDirty(): boolean {
    for (let i = 0; i < this.schedules.length; i++) {
      if (this.schedules[i].checkDirty && this.schedules[i].checkDirty()) {
        return true;
      }
    }
    return false;
  }

  /**
   * Mark ignore check change
   * @param  {any} value
   */
  setIgnoreCheckChange(value: any): void {
    this.ignoreCheckChange = !!value;
  }

  /**
   * Mark ignore check change
   * @param  {any} value
   */
  setIsSavingReport(value: boolean): void {
    this.isSavingReport = value;
  }

  /**
   * Update schedule
   * @param {any} scheduleInstances
   */
  updateSchedule(scheduleInstances: any): void {
    this.schedules = scheduleInstances;
  }

  /**
   * Update list of report owner
   * @param  {any} reportOwners
   */
  @action('updateReportOwner')
  updateReportOwner(reportOwners: any) {
    const owner = last(reportOwners) || {};
    this.owner = owner.fullName;
    this.ownerId = owner.id;
    this.markDirty();
  }

  /**
   * Mark ignore check change
   */
  resetReportId(): void {
    this.reportId = null;
  }

  /**
   * Set report type, clear Category and Sub-category when change report type
   * @param  {number} reportType
   */
  @action('setReportType')
  setReportType(reportType: number) {
    if (this.reportType != reportType) {
      this.reportType = reportType;
      this.clearCategory();
      this.clearSubCategory();
    }
  }

  /**
   * Change global report type
   * @param {number} value // 0 for local or 1 for global
   */
  changeGlobalType(value: number): void {
     this.isGlobal = Boolean(value);
  }

  /**
   * Clear category
   */
  @action('clearCategory')
  clearCategory() {
    this.categoryId = null;
    this.categoryName = null;
  }

  /**
   * Clear sub-category
   */
  @action('clearSubCategory')
  clearSubCategory() {
    this.subCategoryId = null;
    this.subCategoryName = null;
  }


}
export default ReportDesigner;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportDesigner.js